import { useState } from "react";

import {
  useClusters,
  useFacets,
  useStatusCounts,
  useUnmerge,
  type ClusterFilters,
  type ClusterOut,
} from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { decisionSummary, formatScore } from "@/lib/utils";

/**
 * DESIGN §14.3 — clusters and why each member is in one.
 *
 * The decision behind every variant is shown, because §10.1 lets a member join a
 * component through a chain (A≈B, B≈C, no verdict on A–C). Without the reasoning
 * visible, an unmerge is a guess; with it, the reviewer can see whether the model
 * actually compared these two or inferred it.
 */
const PAGE = 25;

export default function Clusters() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [stack, setStack] = useState("");
  const [offset, setOffset] = useState(0);

  const filters: ClusterFilters = {
    limit: PAGE,
    offset,
    ...(search ? { search } : {}),
    ...(status ? { status: [status] } : {}),
    ...(stack ? { stack_path: stack } : {}),
  };
  const { data, isLoading, error } = useClusters(filters);
  const { data: counts } = useStatusCounts();
  const { data: facets } = useFacets();
  const unmerge = useUnmerge();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <header className="flex items-center gap-4">
        <h1 className="text-base font-semibold tracking-tight">Clusters</h1>
        <span className="text-bank-muted font-mono text-xs tabular-nums">
          {isLoading ? "loading…" : `${total} with two or more members`}
        </span>
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Previous
          </Button>
          <Button size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            Next
          </Button>
        </div>
      </header>

      <Panel className="p-3">
        {/* Search matches the canonical's text or any variant's (§10.1: a reviewer
            recalling one question shouldn't have to know it got folded into another's
            cluster). Every filter resets the offset, same reasoning as Questions. */}
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
          <div>
            <FieldLabel htmlFor="c-search">Text contains</FieldLabel>
            <Input
              id="c-search"
              value={search}
              placeholder="closure, middleware, LEFT JOIN…"
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div>
            <FieldLabel htmlFor="c-stack">Stack</FieldLabel>
            <Combobox
              id="c-stack"
              value={stack}
              onChange={(v) => {
                setStack(v);
                setOffset(0);
              }}
              options={[
                { value: "", label: "any" },
                ...(facets?.stack_paths ?? []).map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="c-status">Canonical status</FieldLabel>
            <Combobox
              id="c-status"
              value={status}
              onChange={(v) => {
                setStatus(v);
                setOffset(0);
              }}
              options={[
                { value: "", label: "any" },
                ...Object.keys(counts?.counts ?? {})
                  .sort((a, b) => a.localeCompare(b))
                  .map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>
        </div>
      </Panel>

      <ErrorNote error={error ?? unmerge.error} />

      <div className="min-h-0 flex-1 space-y-3 overflow-auto">
        {items.length === 0 && !isLoading ? (
          <Empty>
            {search || status || stack
              ? "Nothing matches."
              : "No clusters yet. Run the pipeline, or a rescan after changing thresholds."}
          </Empty>
        ) : (
          items.map((cluster) => (
            <ClusterCard
              key={cluster.canonical.id}
              cluster={cluster}
              onUnmerge={(otherId) =>
                unmerge.mutate({ id: cluster.canonical.id, otherId, note: "unmerged in review UI" })
              }
              busy={unmerge.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ClusterCard({
  cluster,
  onUnmerge,
  busy,
}: {
  cluster: ClusterOut;
  onUnmerge: (otherId: number) => void;
  busy: boolean;
}) {
  const { canonical } = cluster;
  return (
    <Panel>
      <PanelHeader
        title={
          <span className="flex items-center gap-2">
            <span className="text-bank-muted font-mono text-[11px] tabular-nums">#{canonical.id}</span>
            <Badge value={canonical.status} />
            <span>canonical</span>
            <span className="text-bank-muted font-mono text-[11px]">
              {canonical.stack_path ?? "—"} / {canonical.primary_concept ?? "—"}
            </span>
          </span>
        }
        actions={
          <span className="text-bank-muted font-mono text-[11px] tabular-nums">
            {cluster.member_count} members · coherence {formatScore(cluster.coherence_score)} · quality{" "}
            {formatScore(canonical.quality_score)}
          </span>
        }
      />
      <div className="p-4">
        <p className="question-text text-sm font-medium">{canonical.question_text}</p>

        <ul className="mt-3 space-y-2">
          {cluster.variants.map(({ question, decision }) => (
            <li
              key={question.id}
              className="border-bank-border grid grid-cols-[1fr_auto] items-start gap-3 border-t pt-2"
            >
              <div className="min-w-0">
                <p className="question-text text-sm">
                  <span className="text-bank-muted mr-1.5 font-mono text-[11px] tabular-nums">
                    ↳ #{question.id}
                  </span>
                  {question.question_text}
                </p>
                <p className="text-bank-muted mt-1 font-mono text-[11px] leading-relaxed">
                  {decisionSummary(decision)}
                </p>
              </div>
              <Button size="sm" variant="danger" disabled={busy} onClick={() => onUnmerge(question.id)}>
                Unmerge
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
